import { useCallback } from 'react';
import { AlertType, ConfirmModal, ManageFolderModal } from '../../../components';
import { useHandleApiResponse, useToast } from '../../../hooks';
import { useDeleteFolderMutation } from '../../../services';
import { Folder } from '../../../types';

const DELETE_FOLDER_ERROR_MSG = 'Failed to delete folder';

export enum FolderActionType {
  EDIT,
  DELETE,
}

interface FolderActionModalsProps {
  folderModal: { type: FolderActionType; folder: Folder };
  closeModal: () => void;
  onDeleteOrMoveSuccess: () => void;
}

/**
 * A component that renders a modal for editing or deleting a folder.
 */
const FolderActionModals = ({ folderModal, closeModal, onDeleteOrMoveSuccess }: FolderActionModalsProps) => {
  const { folder, type } = folderModal;

  const [deleteFolder, { isLoading: isDeletingFolder }] = useDeleteFolderMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const onConfirmDeleteFolder = useCallback(async () => {
    try {
      const response = await deleteFolder(folder.id);
      handleApiResponse({
        response,
        errorMsg: DELETE_FOLDER_ERROR_MSG,
        onSuccess: () => {
          onDeleteOrMoveSuccess();
          closeModal();
        },
      });
    } catch (error) {
      console.error(error);
      showToast({ message: DELETE_FOLDER_ERROR_MSG, type: AlertType.ERROR });
    }
  }, [folder, closeModal, deleteFolder, handleApiResponse, onDeleteOrMoveSuccess, showToast]);

  return (
    <>
      {type === FolderActionType.EDIT && (
        <ManageFolderModal
          closeModal={closeModal}
          folderId={folder.id}
          folderName={folder.name}
          folderParentId={folder.parentId}
          onMoveSuccess={onDeleteOrMoveSuccess}
        />
      )}
      {type === FolderActionType.DELETE && (
        <ConfirmModal
          destructive
          isOpen
          setIsOpen={closeModal}
          onConfirm={onConfirmDeleteFolder}
          title="Delete folder"
          confirmText={
            <>
              Are you sure you want to delete the folder <span className="font-medium">{folder.name}</span>? This action
              cannot be undone.
            </>
          }
          buttonText="Delete"
          isLoading={isDeletingFolder}
        />
      )}
    </>
  );
};

export default FolderActionModals;
